import React from 'react';
import { findAllMeasurementsForFerryTrip, getMeasurementValueFromId } from 'Util/_HumanWritten/MeasurementUtils';
import { CargoEntity, CargoTypeEntity } from 'Models/Entities';
import {
	BookingWizardData,
	BookingWizardInnerDataType,
} from 'Views/Components/_HumanWritten/BookingWizard/FerryTripBookingWizard/BookingWizardData/BaseBookingWizardData';
import LatestVehiclesList from 'Views/Components/_HumanWritten/LatestCargosList/LatestVehiclesList';
import { VehicleBookingWizardData } from '../../BookingWizardData/VehicleBookingWizardData';
import { store } from 'Models/Store';
import { appConfigStore } from 'Models/AppConfigStore';
import { isNotNullOrUndefined } from 'Util/TypeGuards';
import { convertWeight, parseWeightUnit, pickWeightBand, toCanonicalKg } from 'Util/Weight';
import { WizardErrors } from '../../BookingWizardTypes';

export interface PreviousVehicleProps {
	previousCargos: CargoEntity[];
	wizardData: BookingWizardData;
	previousCargo: { previous: boolean };
	saveChanges: (newData: BookingWizardData) => void;
	refetchVehicleTabData: () => void;
	userId: string;
	setPreviousCargoList: (newList: CargoEntity[]) => void;
	cargoTypes: CargoTypeEntity[];
	setInnerWizardErrors: (errors: WizardErrors<BookingWizardInnerDataType>) => void;
}

export function PreviousCargo({
	userId,
	previousCargos,
	previousCargo,
	wizardData,
	saveChanges,
	refetchVehicleTabData,
	setPreviousCargoList,
	cargoTypes,
	setInnerWizardErrors,
}: PreviousVehicleProps) {
	const prefillVehicleInfo = (cargoInfo?: CargoEntity | null) => {
		if (cargoInfo !== undefined && cargoInfo !== null) {
			const newData = new BookingWizardData({ ...wizardData });
			const cargoType = cargoTypes.find(x => x.id === cargoInfo.cargoTypeId);

			if (newData.innerWizardData instanceof VehicleBookingWizardData) {
				newData.innerWizardData.cargoMake = cargoInfo.cargoType.cargoMake;
				newData.innerWizardData.cargoModel = cargoInfo.cargoType.cargoModel;
				newData.innerWizardData.cargoTypeId = cargoInfo.cargoType.id;
				newData.innerWizardData.cargoIdentification = cargoInfo.cargoIdentification;
				newData.innerWizardData.hiredVehicle = false;

				const minLength = getMeasurementValueFromId(cargoType?.minimumLengthId);
				const previousLength = getMeasurementValueFromId(cargoInfo.selectedLengthId);
				if (minLength > previousLength) {
					newData.innerWizardData.vehicleLengthId = cargoType?.minimumLengthId;
				} else {
					newData.innerWizardData.vehicleLengthId = cargoInfo.selectedLengthId;
				}

				const minWeight = getMeasurementValueFromId(cargoType?.minimumWeightId);
				const previousWeight = getMeasurementValueFromId(cargoInfo.selectedWeightId);
				if (minWeight > previousWeight) {
					newData.innerWizardData.vehicleWeightId = cargoType?.minimumWeightId;
				} else {
					newData.innerWizardData.vehicleWeightId = cargoInfo.selectedWeightId;
				}

				// When this vehicle type requires an exact weight, prefill it with the weight recorded
				// for this saved vehicle last time rather than the cargo type's default. Mark it
				// user-provided so VehicleExactWeightInput's auto-prefill effect (which keys off the
				// cargo type) doesn't overwrite it, and align the weight band with that exact weight.
				// Older saved vehicles with no recorded exact weight fall through to the type-default
				// prefill.
				const cargoTypeCharacteristicIds = store.cargoTypeCharacteristicsMap[cargoInfo.cargoType.id] ?? [];
				if (
					appConfigStore.cargoTypeRequiresExactWeight(cargoTypeCharacteristicIds) &&
					isNotNullOrUndefined(cargoInfo.exactWeight) &&
					isNotNullOrUndefined(cargoInfo.exactWeightUnit)
				) {
					const displayUnit = appConfigStore.weightDisplayUnit;
					const previousExactKg = toCanonicalKg(
						Number(cargoInfo.exactWeight),
						parseWeightUnit(cargoInfo.exactWeightUnit),
					);
					newData.innerWizardData.vehicleExactWeight = convertWeight(previousExactKg, 'kg', displayUnit);
					newData.innerWizardData.vehicleExactWeightUnit = displayUnit;
					newData.innerWizardData.vehicleExactWeightUserEdited = true;

					const bandId = pickWeightBand(
						previousExactKg,
						findAllMeasurementsForFerryTrip('WEIGHT', 'vehicle') ?? [],
					);
					if (bandId) {
						newData.innerWizardData.vehicleWeightId = bandId;
					}
				}

				const baseWidth = store.getBaseWidth();

				// Pre-select the larger of the saved width and the vehicle type's minimum
				// width — the type minimum applies even when the saved vehicle has no
				// width recorded (mirrors the length/weight handling above). Widths live
				// in store.widths, not store.measurements, so compare by capacity multiplier.
				const widthMultiplier = (id?: string) => store.widths.find(w => w.id === id)?.capacityMultiplier ?? 0;
				const widthId =
					widthMultiplier(cargoType?.minimumWidthId) > widthMultiplier(cargoInfo.selectedWidthId)
						? cargoType?.minimumWidthId
						: cargoInfo.selectedWidthId;

				if (widthId && baseWidth.id !== widthId) {
					newData.innerWizardData.vehicleWidthId = widthId;
					newData.innerWizardData.showWidthDetails = true;
				} else {
					newData.innerWizardData.vehicleWidthId = '';
					newData.innerWizardData.showWidthDetails = false;
				}
			}
			previousCargo.previous = true;

			// Reset the errors in the booking wizard
			setInnerWizardErrors({});

			saveChanges(newData);
		}
	};

	return (
		<LatestVehiclesList
			userId={userId}
			onSelect={prefillVehicleInfo}
			refetchVehicleTabData={refetchVehicleTabData}
			previousCargos={previousCargos}
			update={setPreviousCargoList}
		/>
	);
}
